import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Tag, Space, Checkbox, message, Card } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, DollarCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const AffiliateUserListModal = ({ visible, onClose, userId }) => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [markingAsPaid, setMarkingAsPaid] = useState(false);
    const [earnings, setEarnings] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    console.log("userId", userId);

    const fetchUserEarnings = async (userId) => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_ADMIN_API_URL}commission/commission-earning/${userId}/AFFILIATE`,
            {
              headers: { 
                'x-product-type': 'PIP_GRID',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('userToken')}`
              }
            }
          );
          if (response.data && response.data.success) {
            setEarnings(response.data.payload)
          }
        } catch (error) {
          console.error('Error fetching user earnings:', error);
        }
      };

    const fetchUsers = async () => {
        if (!userId) return;

        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_ADMIN_API_URL}admin/user-referral/${userId}/AFFILIATE`,
                {
                    params: {
                        page: pagination.current,
                        limit: pagination.pageSize,
                    },
                    headers: {
                        'x-product-type': 'PIP_GRID',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
                    },
                }
            );
      
            if (response.data?.success) {
                setUsers(response.data.payload.data || []);
                setPagination({
                    ...pagination,
                    total: response.data.pagination?.total || 0,
                });
            }
            fetchUserEarnings(userId);
        } catch (error) {
            console.error('Error fetching affiliate users:', error);
            message.error('Failed to fetch affiliate users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log("visible", visible);
        console.log("userId", userId);
        if (visible && userId) {
            fetchUsers();
        }
    }, [visible, userId, pagination.current, pagination.pageSize]);

    const handleMarkAsPaid = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one user');
            return;
        }

        try {
            await axios.post(
                `${process.env.REACT_APP_ADMIN_API_URL}commission/commissionpaid`,
                { commissionIds: selectedRowKeys },
                {
                    headers: {
                        'x-product-type': 'PIP_GRID',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('userToken')}`,
                    },
                }
            );

            message.success('Selected Payments marked as paid successfully');
            fetchUsers();
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Error marking users as paid:', error);
            message.error(error?.message || error?.response?.data?.message || 'Failed to mark users as paid');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: 'Commission',
            dataIndex: 'commissionAmount',
            key: 'commission',
            render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`,
        },
        {
            title: 'Status',
            dataIndex: 'commissionStatus',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'PAID' ? 'green' : 'orange'}>
                    {status}
                </Tag>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: (record) => ({
            disabled: record.commissionStatus === 'PAID',
        }),
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span>Affiliate Users</span>
            <span>{users?.length > 0 ? (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Card 
                  size="small" 
                  style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
                  bodyStyle={{ padding: '4px 12px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <DollarCircleOutlined style={{ color: '#52c41a' }} />
                    <span style={{ fontWeight: 500 }}>${earnings?.totalCommissionEarned || '0.00'}</span>
                    <span style={{ color: '#8c8c8c', fontSize: '12px', marginLeft: '4px' }}>TOTAL</span>
                  </div>
                </Card>
                <Card 
                  size="small" 
                  style={{ background: '#fff2f0', borderColor: '#ffccc7' }}
                  bodyStyle={{ padding: '4px 12px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
                    <span style={{ fontWeight: 500 }}>${earnings?.dueAmount || '0.00'}</span>
                    <span style={{ color: '#8c8c8c', fontSize: '12px', marginLeft: '4px' }}>DUE</span>
                  </div>
                </Card>
                <Card 
                  size="small" 
                  style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}
                  bodyStyle={{ padding: '4px 12px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircleOutlined style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 500 }}>${earnings?.paidAmount || '0.00'}</span>
                    <span style={{ color: '#8c8c8c', fontSize: '12px', marginLeft: '4px' }}>PAID</span>
                  </div>
                </Card>
              </div>
            ) : ""}</span>
            </div>
            {/* <Button 
              type="primary" 
              onClick={handleMarkAsPaid}
              disabled={selectedRowKeys.length === 0 || markingAsPaid}
              loading={markingAsPaid}
              style={{ marginRight: '30px' }}
            >
              Mark as Paid
            </Button> */}
          </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1000}
        >
            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={users}
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
        </Modal>
    );
};

export default AffiliateUserListModal;
